import React from 'react';
import { Field, inputCls } from './FormKit';
import { YesNo } from './EngagementFormFields';
import { DetailGrid, DetailItem } from '../workflow/SectionCard';
import ScreeningTnaReference from './ScreeningTnaReference';

/**
 * The six screening questions — ONE definition for every surface that asks or
 * shows them (docs/modules/workflow-screening.md):
 *
 *   Engagement Registration   New / edit page — asked only while "Does QM
 *                             need to perform screening?" = Yes; Detail shows
 *                             the answers
 *   Screening (standalone)    New / Modify modal — always asked; Detail shows
 *   Management                Screening Records — New / Edit / Detail
 *
 * The answers travel from the registration onto the Screening request its
 * approval opens and, on completion, onto the screening record. They are never
 * written to engagements and never pre-fill the QC6/QC7 form.
 */

export const emptyScreeningAnswers = () => ({
  ultimateParentHqCity: '', ubo: '', isTne: '', tneBasis: '', isTna: '', tnaBasis: '',
});

const triToStr = (v) => (v === true ? 'yes' : v === false ? 'no' : '');

/** Server row (booleans / nulls) → form strings. */
export const screeningAnswersToFields = (r) => ({
  ultimateParentHqCity: (r && r.ultimateParentHqCity) || '',
  ubo: (r && r.ubo) || '',
  isTne: triToStr(r ? r.isTne : null),
  tneBasis: (r && r.tneBasis) || '',
  isTna: triToStr(r ? r.isTna : null),
  tnaBasis: (r && r.tnaBasis) || '',
});

/** Labels — the same wording as the QC6/QC7 form for the TNE / TNA pair. */
export const SCREENING_LABELS = {
  ultimateParentHqCity: 'Ultimate Parent HQ City',
  ubo: 'UBO',
  isTne: 'Is this a transnational entity (TNE)?',
  tneBasis: 'Basis for the TNE assessment',
  isTna: 'Is this a transnational audit (TNA)?',
  tnaBasis: 'Basis for the TNA assessment',
};

/** Required-field gaps `[{ key, label }]` — every one is mandatory. */
export const screeningRequiredGaps = (f, anchor = (k) => k) => {
  const gaps = [];
  const gap = (key) => gaps.push({ key: anchor(key), label: SCREENING_LABELS[key] });
  if (!String(f.ultimateParentHqCity || '').trim()) gap('ultimateParentHqCity');
  if (!String(f.ubo || '').trim()) gap('ubo');
  if (f.isTne === '') gap('isTne');
  if (!String(f.tneBasis || '').trim()) gap('tneBasis');
  if (f.isTna === '') gap('isTna');
  if (!String(f.tnaBasis || '').trim()) gap('tnaBasis');
  return gaps;
};

/** Append the six answers to a FormData (POST / PATCH bodies). */
export const appendScreeningAnswers = (form, f) => {
  form.set('ultimateParentHqCity', f.ultimateParentHqCity || '');
  form.set('ubo', f.ubo || '');
  form.set('isTne', f.isTne === 'yes' ? 'true' : f.isTne === 'no' ? 'false' : '');
  form.set('tneBasis', f.tneBasis || '');
  form.set('isTna', f.isTna === 'yes' ? 'true' : f.isTna === 'no' ? 'false' : '');
  form.set('tnaBasis', f.tnaBasis || '');
  return form;
};

const noAnchor = () => null;

/** The six controls, in form order. `required` off only for read-mostly editors. */
export const ScreeningQuestionFields = ({ f, set, required = true, anchor = noAnchor }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label={SCREENING_LABELS.ultimateParentHqCity} required={required} field={anchor('ultimateParentHqCity')}>
        <input type="text" value={f.ultimateParentHqCity} onChange={(e) => set('ultimateParentHqCity', e.target.value)} className={inputCls} />
      </Field>
      <Field label={SCREENING_LABELS.ubo} required={required} field={anchor('ubo')}>
        <input type="text" value={f.ubo} onChange={(e) => set('ubo', e.target.value)} className={inputCls} />
      </Field>
    </div>
    <Field label={SCREENING_LABELS.isTne} required={required} field={anchor('isTne')}>
      <YesNo value={f.isTne} onChange={(v) => set('isTne', v)} />
    </Field>
    <Field label={SCREENING_LABELS.tneBasis} required={required} field={anchor('tneBasis')}>
      <textarea rows="2" value={f.tneBasis} onChange={(e) => set('tneBasis', e.target.value)} className={inputCls} />
    </Field>
    <Field label={SCREENING_LABELS.isTna} required={required} field={anchor('isTna')}>
      <YesNo value={f.isTna} onChange={(v) => set('isTna', v)} />
    </Field>
    <Field label={SCREENING_LABELS.tnaBasis} required={required} field={anchor('tnaBasis')}>
      <textarea rows="2" value={f.tnaBasis} onChange={(e) => set('tnaBasis', e.target.value)} className={inputCls} />
    </Field>
    {/* 判斷準則站在最後一題底下——四題 TNE／TNA 都答完才是需要對照的時候。 */}
    <ScreeningTnaReference />
  </>
);

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

/** Read-only answers (Detail pages, Management record page). `anchor` maps
 *  keys to the page's comment keys; omit where there is no comment layer. */
export const ScreeningAnswersView = ({ r, anchor = (k) => k }) => (
  <DetailGrid>
    <DetailItem label={SCREENING_LABELS.ultimateParentHqCity} field={anchor('ultimateParentHqCity')}>{r.ultimateParentHqCity || '—'}</DetailItem>
    <DetailItem label={SCREENING_LABELS.ubo} field={anchor('ubo')}>{r.ubo || '—'}</DetailItem>
    <DetailItem label={SCREENING_LABELS.isTne} field={anchor('isTne')}>{yesNo(r.isTne)}</DetailItem>
    <DetailItem label={SCREENING_LABELS.tneBasis} field={anchor('tneBasis')}><span className="whitespace-pre-wrap">{r.tneBasis || '—'}</span></DetailItem>
    <DetailItem label={SCREENING_LABELS.isTna} field={anchor('isTna')}>{yesNo(r.isTna)}</DetailItem>
    <DetailItem label={SCREENING_LABELS.tnaBasis} field={anchor('tnaBasis')}><span className="whitespace-pre-wrap">{r.tnaBasis || '—'}</span></DetailItem>
  </DetailGrid>
);
